import { defineComponent, computed } from "vue";
import { Container } from "aurelia-dependency-injection";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import {
    IPatientFileDetails,
    IUploadPatientDocument,
    IPatientFileHosDetails,
    IUploadPatientHosDocumentQuery
} from "../../../../../resources-vue/vue-interfaces/i-get-patient";
import { AssetsService } from "../../../../../services/assets-service";
import { ToastrService } from "../../../../../services/toastr.service";
import {
    ApplicationEnum,
    DocumentTypeCategory,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../../../../enums/enums";
import { IGetDocumentTypeLean } from "../../../../../resources-vue/vue-interfaces/i-document-type";
import { ReferralService } from "../../../../../services/referral-service";
import { type IPromptOptions } from "../../../../../resources-vue/vue-interfaces/i-prompt";
import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ICompletedPatientChartsResponse } from "../../../../../interfaces/i-patient-service";
import { IGetCompletedPatientChart, PatientService } from "../../../../../services/patient.service";
import { IDownloadCharts } from "../../../../../resources-vue/vue-interfaces/i-patient";
import { IDownloadAsset, AssetsFileDetails } from "../../../../../resources-vue/vue-interfaces/i-asset";

import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import AddDocumentRow from "../PatientDocuments/AddDocumentRow.vue";
import TableEmpty from "../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";

export default defineComponent({
    components: {
        AddDocumentRow,
        TableEmpty
    },

    props: {
        title: { type: String },
        patientId: { type: String },
        accountId: { type: String },
        application: { type: Number },
        uploadDocument: { type: Function },
        getDocuments: { type: Function },
        patientVersionId: { type: String },
        providerId: { type: String },
        deleteDocument: { type: Function },
        isHistoryPage: { type: Boolean, default: false },
        isReferralPage: { type: Boolean, default: false },
        isEditPatient: { type: Boolean, default: false },
        isLoadingComment: { type: Boolean, default: false },
        commentDocument: { type: Function },
        documentType: { type: String },
        referral: { default: null }
    },
    provide() {
        return {
            v: computed(() => this.v$)
        };
    },

    data() {
        return {
            _patientService: null as PatientService,
            _assetsService: null as AssetsService,
            _toastrService: null as ToastrService,
            _referralService: null as ReferralService,
            _permissionManager: null as PermissionManager,
            fileDetails: [],
            uploading: false,
            loadingDocuments: false,
            patientDocuments: [],
            addPatientDocumentsPermission: false,
            searchedTypes: [],
            DocumentTypeCategoryEnum: DocumentTypeCategory,
            deletePatientDocumentsPermission: false,
            uploadFile: [],
            moreFiles: [],
            loadingCount: 5,
            patientCharts: [],
            v$: useVuelidate(),
            formatDate
        };
    },

    validations() {
        return {
            fileDetails: this.fileDetails.map(() => ({
                type: { required }
            }))
        };
    },

    watch: {
        uploadFile(newValue: File[], oldValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = files.map((file: File) => ({ name: file.name, type: null, file: file }));
        },

        moreFiles(newValue: File[], oldValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = this.fileDetails.concat(
                files.map((file: File) => ({ name: file.name, type: null, file: file }))
            );
        }
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._patientService = Container.instance.get(PatientService);
    },

    async mounted() {
        await this.loadSearchedTypes();
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.documents,
            PermissionActionEnum.canAdd,
            this.accountId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.documents,
            PermissionActionEnum.canDelete,
            this.accountId
        );
        await this.loadFilesList();
    },

    methods: {
        async loadSearchedTypes() {
            if (!this.patientId) {
                this.searchedTypes = [];
            }
            let category = Number(this.DocumentTypeCategoryEnum.enum.Patient);
            this.loadingDocuments = true;
            let searchedTypes: IGetDocumentTypeLean[] = await this._referralService.getDocumentTypesList({
                providerIds: [this.providerId],
                application: this.application,
                accountId: this.accountId,
                category
            });
            this.loadingDocuments = false;
            if (searchedTypes) {
                this.searchedTypes = searchedTypes.map((documentType: IGetDocumentTypeLean) => ({
                    value: documentType.id,
                    name: documentType.type
                }));
                if (this.isEditPatient || this.isHistoryPage) {
                    this.searchedTypes = this.searchedTypes.filter(
                        (documentType) =>
                            documentType.name.toLowerCase() === "labs" ||
                            documentType.name.toLowerCase() === "scans/tests"
                    );
                }
            } else {
                this.searchedTypes = [];
            }
        },

        removeFile(index: number) {
            this.fileDetails = this.fileDetails.filter((file, indx) => index != indx);
        },

        removeAllFiles() {
            this.fileDetails = [];
        },

        moreFilesChanged(newValue: File[], oldValue: File[]) {
            let files: File[] = Array.from(newValue);
            this.fileDetails = this.fileDetails.concat(
                files.map((file: File) => ({ name: file.name, type: null, file: file }))
            );
        },

        uploadFilesChanged(event: any) {
            this.uploadFile = event.target.files;
        },

        moreFilesAdded(event: any) {
            this.moreFiles = event.target.files;
        },

        async deleteSelectedDocument(documentId: string) {
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete?",
                    okText: "Yes",
                    cancelText: "No"
                };

                this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        showHeader: false
                    },
                    data: { promptOptions },
                    onClose: async (confirm) => {
                        if (confirm.data) {
                            try {
                                await this.deleteDocument(this.patientId, documentId);
                                this.loadFilesList();
                                this._toastrService.success({
                                    message: `The document has been successfully deleted.`,
                                    title: `Document Deleted`
                                });
                            } catch (err) {
                                this._toastrService.error({
                                    message: `Failed to delete document`,
                                    title: `Try Again`
                                });
                            }
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Document`,
                    message: `There was a problem while deleting the document. Please try again.`
                });
            }
        },

        getFileIcon(type: string) {
            if (!type) {
                return "fa-file";
            }
            if (type.match("image.*")) {
                return "fa-file-image";
            } else if (type.includes("wordprocessing")) {
                return "fa-file-word";
            } else if (type.includes("spreadsheet")) {
                return "fa-file-excel";
            } else if (type.includes("pdf")) {
                return "fa-file-pdf";
            } else {
                return "fa-file";
            }
        },

        async uploadDocuments() {
            const validated = await this.v$.$validate();

            if (validated) {
                if (this.patientId) {
                    try {
                        if (this.application == ApplicationEnum.AxxessHospiceFE) {
                            this.uploading = true;
                            this.fileDetails.forEach((file, index) => {
                                file.attachmentNumber = index;
                            });
                            let assets: AssetsFileDetails[] = this.fileDetails.map((file) => {
                                return {
                                    FileName: file.name,
                                    FileSize: file.file.size,
                                    AttachmentNumber: file.attachmentNumber
                                };
                            });
                            const { assetDetails } = await this._assetsService.createHosAsset({
                                Assets: assets,
                                AccountId: this.accountId,
                                Application: this.application
                            });
                            for (const file of this.fileDetails) {
                                let assetDetail = assetDetails.find(
                                    (asset) => asset.AttachmentNumber == file.attachmentNumber
                                );
                                if (!assetDetail || !assetDetail.AssetId) {
                                    throw new Error("Asset Creation Unsuccessful.");
                                }
                                const currentFile = file.file;
                                const currentFileWithUpdatedFileName = new File([currentFile], file.name, {
                                    type: currentFile.type
                                });
                                await this._assetsService.upload(
                                    assetDetail.Token,
                                    currentFileWithUpdatedFileName,
                                    this.application
                                );
                            }
                            let selectedFileDetailsWithAssetId: IPatientFileHosDetails[] = this.fileDetails.map(
                                (file) => {
                                    let assetDetail = assetDetails.find(
                                        (asset) => asset.AttachmentNumber == file.attachmentNumber
                                    );
                                    let assetId = assetDetail.AssetId;
                                    return { ...file, assetId };
                                }
                            );
                            let patientUploadingDocs: IUploadPatientDocument[] = selectedFileDetailsWithAssetId.map(
                                (fileDetail) => {
                                    let AssetId = fileDetail.assetId;
                                    let DocumentName = fileDetail.name;
                                    let UploadType = fileDetail.type.value;
                                    let Key = fileDetail.attachmentNumber;
                                    return { AssetId, DocumentName, UploadType, Key };
                                }
                            );
                            await this.uploadDocument(this.patientId, {
                                Documents: patientUploadingDocs,
                                AccountId: this.accountId,
                                Application: this.application
                            });
                            this.removeAllFiles();
                            this.loadFilesList();
                            this._toastrService.success({
                                title: `Upload Successful`,
                                message: `The files have been successfully uploaded`
                            });
                        } else if (this.application == ApplicationEnum.AxxessPalliative) {
                            this.uploading = true;
                            await this.asyncForEach(this.fileDetails, async (fileDetail) => {
                                if (fileDetail?.type) {
                                    let assetId = await this._assetsService.uploadFile(
                                        fileDetail.file,
                                        this.accountId,
                                        this.application
                                    );
                                    await this.uploadDocument(this.patientId, {
                                        assetId: assetId,
                                        documentName: fileDetail.name,
                                        uploadType: fileDetail.type.value,
                                        patientVersionId: this.patientVersionId,
                                        application: this.application,
                                        accountId: this.accountId
                                    });
                                } else {
                                    throw new Error("No Type");
                                }
                            });
                            this.removeAllFiles();
                            this.loadFilesList();
                            this._toastrService.success({
                                title: `Upload Successful`,
                                message: `The files have been successfully uploaded`
                            });
                        }
                    } catch (error) {
                        await this.showUploadError();
                    }
                    this.uploading = false;
                } else {
                    await this.showUploadError();
                }
            } else {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required fields.`
                });
            }
        },

        async asyncForEach(array: IPatientFileDetails[], callback: (item: IPatientFileDetails) => void) {
            for (let index = 0; index < array.length; index++) {
                await callback(array[index]);
            }
        },

        async loadFilesList() {
            if (this.patientId) {
                this.loadingDocuments = true;
                const id = this.isReferralPage
                    ? this.patientId
                    : this.searchedTypes.find((type: any) => type.name === this.documentType).value;
                const response = await this.getDocuments(id);
                if (this.isReferralPage) {
                    this.patientDocuments = response;
                } else if (!response.data) {
                    this.patientDocuments = [];
                    this.loadingDocuments = false;
                } else {
                    this.patientDocuments = response.data;
                    this.loadingDocuments = false;
                }
                const data = await this.getCompletedPatientChart();
                if (data) {
                    this.patientCharts = JSON.parse(JSON.stringify(data)).Data;
                }
                this.documentConcatentation(this.patientCharts);
                this.loadingDocuments = false;
            }
        },

        async documentConcatentation(patientChart: Array<object>) {
            const modifiedObjArr = patientChart.map((item: ICompletedPatientChartsResponse) => {
                const assetId = item.AssetId.split("-")[3];
                return {
                    ...item,
                    documentName: `${this.referral.firstName}_${this.referral.lastName}_${assetId}_PatientChart`,
                    uploadType: item.Format,
                    modifiedOn: item.Completed,
                    isPatientChart: true
                };
            });

            if (modifiedObjArr) {
                this.patientDocuments = this.patientDocuments
                    ? modifiedObjArr.concat(this.patientDocuments)
                    : modifiedObjArr;
            }
        },

        async getCompletedPatientChart() {
            const getCompletedPatientChartQuery: IGetCompletedPatientChart = {
                referralId: this.referral?.id,
                application: this.referral?.application,
                agencyId: this.referral?.accountId
            };

            return await this._patientService.getCompletedPatientCharts(getCompletedPatientChartQuery);
        },

        async openSelectedDocument(documents: IDownloadCharts) {
            const docId =
                documents.Application == ApplicationEnum.AxxessHospiceFE ||
                documents.Application == ApplicationEnum.AxxessPalliative
                    ? documents.Id
                    : documents.AssetId;
            const downloadDocument: IDownloadAsset = {
                assetId: docId,
                application: documents.Application || this.referral?.application,
                agencyId: documents.AgencyId
            };

            return await this._assetsService
                .downloadPatientChart(downloadDocument)
                .then((response: any) => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.body], {
                            type:
                                downloadDocument.application == ApplicationEnum.HomeCare
                                    ? "application/pdf"
                                    : "application/zip"
                        })
                    );
                    window.open(url, "_blank");
                    this._toastrService.success({
                        message: `The requested patient/client chart document is downloaded.`
                    });
                    this.loadingDocuments = false;
                })
                .catch((error: any) => {
                    if (error && documents.Status === "Running") {
                        this._toastrService.info({
                            message: `The requested patient/client chart document is in-progress, you will be notified when the document is completed.`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Opening Document Failed`,
                            message: `Opening Document Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                });
        },

        async showUploadError() {
            this._toastrService.error({
                title: `Error`,
                message: `There was a problem while uploading files. Please try again.`
            });
        },

        async createComment() {
            await this.commentDocument();
        },

        isFieldRequired(index: number) {
            return this.v$.$errors.find((error: any) =>
                error.$propertyPath === `fileDetails.${index}.type` ? true : false
            );
        }
    }
});
